import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import type { TextoConsentimiento, VistaExpediente } from '@api/Consents/Consent';
import SignatureField, { SignatureFieldHandle } from '@src/components/SignatureField';

/**
 * El texto del consentimiento tal como lo manda el server (`ConsentTexto.ts`, en la versión del
 * expediente; desde la v2 es el contrato general): lo que se lee aquí es exactamente lo que se imprime
 * en el PDF y lo que cubre el hash de la firma.
 */
export const TextoConsentimientoView: React.FC<{ texto: TextoConsentimiento }> = ({ texto }) => (
    <Stack spacing={1.5} sx={{ '& p': { lineHeight: 1.6 } }}>
        {texto.parrafos.map((p, i) => (
            <Typography key={i} variant="body2" component="p">
                {p}
            </Typography>
        ))}
        <Typography fontWeight={700}>{texto.subtitulos.perfeccionamiento}</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            {texto.perfeccionamiento.map((p, i) => (
                <Typography key={i} component="li" variant="body2">
                    {p}
                </Typography>
            ))}
        </Box>
        <Typography fontWeight={700}>{texto.subtitulos.laser}</Typography>
        <Stack spacing={0.25}>
            {texto.laser.map((p, i) => (
                <Typography key={i} variant="body2">
                    {p}
                </Typography>
            ))}
        </Stack>
        <Typography variant="body2" component="p" fontWeight={600}>
            {texto.aceptacion}
        </Typography>
        <Typography fontWeight={700}>{texto.subtitulos.observaciones}</Typography>
        <Typography variant="body2" component="p" color="text.secondary">
            {texto.observaciones}
        </Typography>
    </Stack>
);

/**
 * Leer y firmar el consentimiento (una vez por expediente). Siempre le habla a la clienta: firma
 * ella, en su app o con el QR que le muestra el Punto de Venta (el server registra si fue asistida).
 * Sin tutor: estos procedimientos solo se hacen a mayores de edad (Irving, 14/oct/2026).
 */
export const FirmaConsentimiento: React.FC<{
    vista: VistaExpediente;
    firmando: boolean;
    onFirmar: (firma: string) => void;
}> = ({ vista, firmando, onFirmar }) => {
    const pad = useRef<SignatureFieldHandle>(null);
    const [vacio, setVacio] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const firmar = () => {
        setError(null);
        const firma = pad.current?.toPng();
        if (!firma) {
            setError('Firma en el recuadro.');
            return;
        }
        onFirmar(firma);
    };

    return (
        <Stack spacing={2.25}>
            <Box
                sx={{
                    borderRadius: 2,
                    // En el teléfono el texto va a todo lo ancho; el recuadro solo en pantallas grandes.
                    p: { xs: 0, sm: 2.25 },
                    bgcolor: { xs: 'transparent', sm: 'action.hover' },
                    maxHeight: { xs: 'none', sm: 460 },
                    overflowY: { xs: 'visible', sm: 'auto' }
                }}
            >
                <TextoConsentimientoView texto={vista.consentimiento.texto} />
            </Box>

            <SignatureField ref={pad} label="Tu firma" onChange={setVacio} disabled={firmando} />

            <Typography variant="caption" color="text.secondary">
                Al firmar aceptas el consentimiento. Queda guardado en tu expediente con la fecha y la hora.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <Button variant="contained" size="large" onClick={firmar} disabled={firmando || vacio}>
                {firmando ? 'Firmando…' : 'Firmar mi contrato'}
            </Button>
        </Stack>
    );
};
